import toUpper from 'lodash/toUpper'
import { pipe, evolve, values as ramdaValues, map, find, propEq } from 'ramda'
import { renameKeys } from '@/utils'
import toArray from '@/utils/to-array'

type AsKeys<T> = Record<string, T>

type AsArray<T> = T[]

type RawBase = {
  txt_id: string
  name: string
}

type FormattedBase = {
  value: string
  label: string
}

type AsRaw<T> = Omit<T, keyof FormattedBase> & RawBase
type AsFormatted<T> = Omit<T, keyof RawBase> & FormattedBase

type State = {
  txt_id: string
  name: string
}

export type Country = {
  txt_id: string
  name: string
  capital: string
  Continent: string
  top_level_domain: string
  calling_code: string
  currency_code: string
  currency_name: string
  states?: AsKeys<State>
}

const toFormatted = <T extends RawBase>(value: T): AsFormatted<T> =>
  renameKeys({ txt_id: 'value', name: 'label' }, value) as AsFormatted<T>

export const getFormattedCountries = pipe<
  AsArray<AsKeys<AsRaw<Country>>>,
  AsArray<AsRaw<Country>>,
  AsArray<AsFormatted<Country>>
>(ramdaValues, map<AsRaw<Country>, AsFormatted<Country>>(toFormatted))

const formatStates = pipe<
  AsArray<AsKeys<AsRaw<State>>>,
  AsArray<AsRaw<State>>,
  AsArray<AsFormatted<State>>
>(
  ramdaValues,
  map<AsRaw<State>, AsFormatted<State>>(
    pipe<AsArray<AsRaw<State>>, AsFormatted<State>, AsFormatted<State>>(
      toFormatted,
      evolve({ value: toUpper })
    )
  )
)

export const getCountry = (
  countryCode: string,
  countries: AsKeys<AsRaw<Country>>
): AsRaw<Country> | undefined =>
  find<AsRaw<Country>>(propEq('txt_id', countryCode.toLowerCase()), toArray(countries))

export const getState = (
  stateCode: string,
  states: AsKeys<AsRaw<State>>
): AsRaw<State> | undefined =>
  find<AsRaw<State>>(propEq('txt_id', stateCode.toUpperCase()), toArray(states))

export const getFormattedStates = (
  countries: AsKeys<AsRaw<Country>>,
  countryCode: string
): AsArray<AsFormatted<State>> => {
  const countryData = getCountry(countryCode, countries)
  if (!countryData?.states) return []
  return formatStates(countryData.states)
}

export const PROVINCES = [
  {
    value: 'AB',
    label: 'Alberta'
  },
  {
    value: 'BC',
    label: 'British Columbia'
  },
  {
    value: 'MB',
    label: 'Manitoba'
  },
  {
    value: 'NB',
    label: 'New Brunswick'
  },
  {
    value: 'NL',
    label: 'Newfoundland and Labrador'
  },
  {
    value: 'NT',
    label: 'Northwest Territories'
  },
  {
    value: 'NS',
    label: 'Nova Scotia'
  },
  {
    value: 'NU',
    label: 'Nunavut'
  },
  {
    value: 'ON',
    label: 'Ontario'
  },
  {
    value: 'PE',
    label: 'Prince Edward Island'
  },
  {
    value: 'QC',
    label: 'Quebec'
  },
  {
    value: 'SK',
    label: 'Saskatchewan'
  },
  {
    value: 'YT',
    label: 'Yukon Territory'
  }
]
